import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PortalComment,
  PortalCommentAuthorType,
  PortalOutsideApproval,
  PortalReviewDecision,
  PortalReviewRoundStatus,
} from "@/types/clientPortal.type";
import type {
  CalendarBriefKind,
  ProductionBriefBody,
} from "@/types/productionBrief.type";
import { clientBriefsKey, markBriefDecided, type PortalSource } from "./clientPortalApi";
import { commentBody, type ClientCommentInput } from "./useClientPortal";
import { POLL_PORTAL_MS } from "@/lib/realtime";
import { livePoll, useCalendarLive } from "@/app/routes/dashboard/content-calendar/api/calendarLive";
import { scrubVendorTermsDeep } from "@/lib/vendorTerms";

/**
 * The production-brief half of the client portal.
 *
 * Same token, same axios instance and same "a token IS one month" rule as
 * `useClientPortal` — this is the complete Creative Brief the client reviews:
 * selected concept, execution direction, bilingual copy and platform intent.
 *
 * Note what is absent, as on the team side: there is no regenerate. A declined
 * brief is rewritten BY HAND, so the only thing a decline produces here is a
 * reason string that a person reads.
 */

export type ClientBriefPhase = "awaiting_you" | "decided" | "revised";

/**
 * Month-wide TALLY plus the comment gate.
 *
 * `decisionsLocked` is a month-level leftover — deciding is answered per brief
 * by `brief.actionable`, because one brief being accepted says nothing about
 * the next. `commentsOpen` genuinely is month-level: while the team is
 * rewriting anything, the conversation stays open, and that is where the
 * rewrite gets its detail.
 */
export interface ClientBriefReview {
  roundNumber: number;
  status: PortalReviewRoundStatus;
  sentAt: string;
  total: number;
  decided: number;
  /** Briefs waiting on this client right now. */
  awaiting: number;
  /** @deprecated Read `brief.actionable` instead. */
  decisionsLocked: boolean;
  commentsOpen: boolean;
}

export interface ClientBriefComment {
  id: string;
  authorType: PortalCommentAuthorType;
  authorName: string;
  body: string;
  /** Which version of the brief this was written against. */
  briefVersion: number;
  createdAt: string;
  /** Thread fields (backend #128), as on concept comments. */
  anchor?: PortalComment["anchor"];
  parentId?: string | null;
  replies?: ClientBriefComment[];
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  editedAt?: string | null;
  deleted?: boolean;
  mine?: boolean;
}

export interface ClientBriefDecision {
  decision: PortalReviewDecision;
  /** Present on a decline — what the rewrite is being written against. */
  reason: string | null;
  clientName: string;
  decidedAt: string;
  /** Carried forward from an earlier round: the brief didn't change. */
  carriedOver: boolean;
}

/** One immutable Creative Brief version that reached the client. */
export interface ClientBriefVersion {
  version: number;
  body: ProductionBriefBody;
  reason: string | null;
}

/** The version this one replaced, retained for the current API contract. */
export interface ClientBriefPreviousVersion extends ClientBriefVersion {}

export interface ClientBrief {
  briefId: string;
  slotId: string;
  ideaTitle: string;
  scheduledAt: string;
  kind: CalendarBriefKind;
  phase: ClientBriefPhase;
  /** THE per-card signal: may this client decide on this brief right now? */
  actionable: boolean;
  version: number;
  body: ProductionBriefBody;
  decision: ClientBriefDecision | null;
  /**
   * Every recoverable client-visible version, ascending. The client accepts
   * this additive field when the API provides it and falls back to
   * `previousVersion + current` while older servers roll forward.
   */
  versions?: ClientBriefVersion[];
  previousVersion: ClientBriefPreviousVersion | null;
  comments: ClientBriefComment[];
  /** #129: the approval they gave outside Creative OS, as the team recorded it. */
  approvedOutside?: PortalOutsideApproval | null;
  /** Who accepted the brief while it stands accepted (see PortalSlot.approvalSource). */
  acceptanceSource?: "CLIENT" | "TEAM" | null;
  /** The day the team asked for an answer by, while the brief waits on the client. */
  answerBy?: string | null;
  /** The team's note on the send, while the brief waits on the client. */
  note?: string | null;
}

export interface ClientBriefsMonth {
  planId: string;
  year: number;
  month: number;
  review: ClientBriefReview | null;
  briefs: ClientBrief[];
}

/**
 * The reason on a decline is optional since backend #131: the client says what
 * to change in comments. Kept as a constant so every form reads the same floor.
 */
export const DECLINE_REASON_MIN_LENGTH = 0;

/**
 * Both gates default CLOSED when there is no round. No review means nothing was
 * ever sent to this client, and the failure that matters is accepting a decision
 * the server will reject — not hiding a button for a moment.
 *
 * `canDecide` now takes the BRIEF, because that is the level the answer lives
 * at: passing none asks the month-wide question, which only the header uses.
 */
export function briefGates(
  review: ClientBriefReview | null | undefined,
  brief?: ClientBrief | null,
): {
  canDecide: boolean;
  canComment: boolean;
} {
  return {
    canDecide: brief ? brief.actionable : (review?.awaiting ?? 0) > 0,
    canComment: review ? review.commentsOpen : false,
  };
}

// Keyed by token alone, like the rest of the portal: the token is the month.
const briefsKey = clientBriefsKey;

export function useClientBriefs(source: PortalSource) {
  const token = source.id;
  const live = useCalendarLive();
  return useQuery({
    queryKey: briefsKey(token),
    queryFn: async () => {
      const res = await source.get<ClientBriefsMonth>("briefs");
      return scrubVendorTermsDeep(res.data);
    },
    enabled: !!token,
    // Global retry policy: a bad/revoked token (401/403) never retries; an
    // unreachable backend retries up to the cap, then the outage page shows.
    // Team edits, comment replies, recalls and re-sends all land server-side
    // while this tab sits open. Slow poll; only a dead token (401/403) stops
    // it — transient errors keep polling so the page self-heals. While the
    // brand is live the calendar's realtime hints refresh it instead.
    refetchInterval: (query) => (source.isDead(query.state.error) ? false : livePoll(live, POLL_PORTAL_MS)),
    refetchOnWindowFocus: !live,
  });
}

/** The same token-scoped context query `useClientPortal.ts` populates — read-only here, just for the viewer's display name. */
const clientContextKey = (token: string) => ["clientPortal", "context", token];

/**
 * Approve or decline one brief.
 *
 * Optimistic on the brief's own answer, the same way the ideas round's
 * `useSubmitDecision` is: this brief is now decided, full stop. Whether that
 * closes the round or flips the month stays server-decided and is reconciled
 * by the refetch in `onSuccess` — only the slow, certain part is guessed here.
 */
export function useSubmitBriefDecision(source: PortalSource) {
  const token = source.id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      slotId: string;
      decision: PortalReviewDecision;
      reason?: string;
    }) => {
      const res = await source.post<{
        decision: PortalReviewDecision;
        roundClosed?: boolean;
      }>(`/slots/${payload.slotId}/brief-decision`, { decision: payload.decision, reason: payload.reason ?? null });
      return res.data;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: briefsKey(token) });
      const context = qc.getQueryData<{ client: { name: string | null; email: string } }>(
        clientContextKey(token),
      );
      const clientName = context?.client.name || context?.client.email || "You";
      const previous = markBriefDecided(qc, token, {
        slotId: payload.slotId,
        decision: payload.decision,
        reason: payload.reason ?? null,
        clientName,
      });
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) qc.setQueryData(briefsKey(token), context.previous);
      // A refused decision means the round moved under us — fetch the truth
      // right away rather than waiting out the slow poll.
      qc.invalidateQueries({ queryKey: briefsKey(token) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: briefsKey(token) });
    },
  });
}

/**
 * Post a comment on a brief. Optimistically appended so the client sees their
 * own note land immediately; reconciled against the server's copy — with any
 * team reply that arrived alongside it — by the invalidation in `onSuccess`.
 */
export function useAddClientBriefComment(
  source: PortalSource,
  /** `optimistic: false`: the caller shows the post itself (see useAddClientComment). */
  options: { optimistic?: boolean } = {},
) {
  const token = source.id;
  const qc = useQueryClient();
  const optimistic = options.optimistic ?? true;
  return useMutation({
    mutationFn: async (payload: { briefId: string } & ClientCommentInput) => {
      await source.post(`/briefs/${payload.briefId}/comments`, commentBody(payload));
    },
    onMutate: async (payload) => {
      if (!optimistic) return { previous: undefined };
      await qc.cancelQueries({ queryKey: briefsKey(token) });
      const previous = qc.getQueryData<ClientBriefsMonth>(briefsKey(token));
      const context = qc.getQueryData<{ client: { name: string | null; email: string } }>(
        clientContextKey(token),
      );
      const authorName = context?.client.name || context?.client.email || "You";
      const target = previous?.briefs.find((b) => b.briefId === payload.briefId);

      qc.setQueryData<ClientBriefsMonth>(briefsKey(token), (data) => {
        if (!data) return data;
        const optimisticComment: ClientBriefComment = {
          id: `optimistic-${Date.now()}`,
          authorType: "CLIENT",
          authorName,
          body: payload.body,
          briefVersion: target?.version ?? 1,
          createdAt: new Date().toISOString(),
        };
        return {
          ...data,
          briefs: data.briefs.map((b) =>
            b.briefId === payload.briefId
              ? { ...b, comments: [...b.comments, optimisticComment] }
              : b,
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _payload, context) => {
      if (context?.previous) qc.setQueryData(briefsKey(token), context.previous);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: briefsKey(token) });
    },
  });
}
